if (!customElements.get("nmn-product-reviews")) {
  customElements.define(
    "nmn-product-reviews",
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.measureNeeded = false;
        this.cardStep = 0;
        this.controller = new AbortController();
        this.track = this.querySelector("[data-reviews-track]");
        this.cards = Array.from(this.querySelectorAll("[data-review-card]"));
        this.controls = this.querySelector("[data-reviews-controls]");
        this.range = this.querySelector("[data-reviews-range]");
        this.buttons = Array.from(this.querySelectorAll("[data-reviews-direction]"));
        if (!this.track || !this.cards.length || !this.controls || !this.range) return;

        const options = { signal: this.controller.signal };
        this.buttons.forEach((button) => {
          button.addEventListener("click", () => this.navigate(Number(button.dataset.reviewsDirection)), options);
        });
        this.track.addEventListener("keydown", (event) => {
          if (event.target !== this.track) return;
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          if (event.key === "Home" || event.key === "End") {
            this.scrollToCard(event.key === "Home" ? 0 : this.cards.length - 1);
          } else {
            this.navigate(event.key === "ArrowLeft" ? -1 : 1);
          }
        }, options);
        this.track.addEventListener("scroll", () => {
          this.scheduleUpdate();
        }, { ...options, passive: true });
        document.addEventListener("shopify:block:select", (event) => {
          const card = event.target.closest("[data-review-card]");
          if (!card || !this.contains(card)) return;
          this.scrollToCard(this.cards.indexOf(card), "instant");
        }, options);
        this.observer = new ResizeObserver(() => this.scheduleUpdate(true));
        this.observer.observe(this.track);
        this.observer.observe(this.cards[0]);
        this.setAttribute("data-reviews-enhanced", "");
        this.scheduleUpdate(true);
      }

      scheduleUpdate(measure = false) {
        this.measureNeeded ||= measure;
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      navigate(direction) {
        this.scrollToCard(this.activeIndex + direction * this.pageSize);
      }

      scrollToCard(index, behavior) {
        if (!this.cardStep) return;
        const targetIndex = Math.max(0, Math.min(index, this.cards.length - this.pageSize));
        this.track.scrollTo({
          left: targetIndex * this.cardStep,
          behavior: behavior || (window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth"),
        });
      }

      update() {
        // Read geometry before changing controls, and only remeasure on resize.
        if (this.measureNeeded) {
          const cardWidth = this.cards[0].getBoundingClientRect().width;
          if (!cardWidth) return;
          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          const trackWidth = this.track.clientWidth;
          const scrollWidth = this.track.scrollWidth;
          this.cardStep = cardWidth + gap;
          this.pageSize = Math.min(this.cards.length, Math.max(1, Math.round((trackWidth + gap) / this.cardStep)));
          this.maxScroll = Math.max(0, scrollWidth - trackWidth);
          this.measureNeeded = false;
        }
        if (!this.cardStep) return;
        const scrollLeft = this.track.scrollLeft;
        this.activeIndex = Math.max(0, Math.min(
          Math.round(scrollLeft / this.cardStep),
          this.cards.length - this.pageSize,
        ));
        const last = Math.min(this.activeIndex + this.pageSize, this.cards.length);
        const range = last === this.activeIndex + 1 ? String(last) : `${this.activeIndex + 1}–${last}`;
        if (this.range.textContent !== range) this.range.textContent = range;
        const hidden = this.maxScroll <= 1;
        if (this.controls.hidden !== hidden) this.controls.hidden = hidden;
        this.buttons.forEach((button) => {
          const disabled = Number(button.dataset.reviewsDirection) < 0
            ? scrollLeft <= 1
            : scrollLeft >= this.maxScroll - 1;
          if (button.disabled !== disabled) button.disabled = disabled;
        });
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.removeAttribute("data-reviews-enhanced");
      }
    },
  );
}
